import { create } from 'zustand'
import type { QueryOutcome } from '@/lib/api'
import type { QueryRoute } from '@/lib/api/queryApi'
import { createId } from '@/lib/utils/id'

/**
 * This store is deliberately just the *live* thread — the one currently on
 * screen. It is not itself persisted; `chatsStore` mirrors it into
 * localStorage exchange-by-exchange so the sidebar's chat list survives a
 * reload, and `loadSession` swaps a stored chat back in here to make it live.
 *
 * The backend owns the real conversation state, keyed by `conversation_id` in
 * an in-process LangGraph store (`app/chat/conversation_graph.py`). Reopening
 * an old chat after a backend restart will still show its past exchanges —
 * that's `chatsStore`'s doing — but a *new* follow-up sent to that
 * conversation id may lose context the backend no longer holds. That's a
 * backend limitation, not something the client can paper over.
 */

export type ExchangeStatus = 'pending' | 'success' | 'error' | 'cancelled'

export interface ExchangeError {
  message: string
  kind: string
  code?: string
  status?: number
}

export interface Exchange {
  id: string
  query: string
  route: QueryRoute
  createdAt: number
  status: ExchangeStatus
  outcome?: QueryOutcome
  error?: ExchangeError
  durationMs?: number
}

export interface SessionState {
  conversationId: string
  exchanges: Exchange[]
  startedAt: number

  startExchange: (input: { query: string; route: QueryRoute }) => string
  resolveExchange: (id: string, outcome: QueryOutcome, durationMs: number) => void
  failExchange: (id: string, error: ExchangeError, durationMs: number) => void
  cancelExchange: (id: string) => void
  removeExchange: (id: string) => void
  /** Discards the thread and mints a new conversation id, cutting server-side
   *  context loose along with it. */
  newSession: () => void
  /** Swaps in a previously stored chat as the live thread. */
  loadSession: (input: { conversationId: string; exchanges: Exchange[] }) => void
}

function patch(
  exchanges: Exchange[],
  id: string,
  update: Partial<Exchange>,
): Exchange[] {
  return exchanges.map((exchange) =>
    exchange.id === id ? { ...exchange, ...update } : exchange,
  )
}

export const useSessionStore = create<SessionState>()((set) => ({
  conversationId: createId(),
  exchanges: [],
  startedAt: Date.now(),

  startExchange: ({ query, route }) => {
    const id = createId()
    set((state) => ({
      exchanges: [
        ...state.exchanges,
        { id, query, route, createdAt: Date.now(), status: 'pending' },
      ],
    }))
    return id
  },

  resolveExchange: (id, outcome, durationMs) =>
    set((state) => ({
      exchanges: patch(state.exchanges, id, {
        status: 'success',
        outcome,
        durationMs,
      }),
    })),

  failExchange: (id, error, durationMs) =>
    set((state) => ({
      exchanges: patch(state.exchanges, id, {
        status: 'error',
        error,
        durationMs,
      }),
    })),

  cancelExchange: (id) =>
    set((state) => ({
      exchanges: patch(state.exchanges, id, { status: 'cancelled' }),
    })),

  removeExchange: (id) =>
    set((state) => ({
      exchanges: state.exchanges.filter((exchange) => exchange.id !== id),
    })),

  newSession: () =>
    set({
      conversationId: createId(),
      exchanges: [],
      startedAt: Date.now(),
    }),

  loadSession: ({ conversationId, exchanges }) =>
    set({ conversationId, exchanges, startedAt: Date.now() }),
}))

export const selectIsRunning = (state: SessionState): boolean =>
  state.exchanges.some((exchange) => exchange.status === 'pending')

export const selectLastSuccessful = (state: SessionState): Exchange | undefined =>
  [...state.exchanges].reverse().find((exchange) => exchange.status === 'success')
